import { useState, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { timeAgo } from '../../utils/activityUtils';

const PAGE_SIZE = 10;

/**
 * Maps collection names to human-readable filter labels.
 * Keys match the collectionName field written to activityFeed documents.
 */
const FILTER_LABELS = {
  bottleFeeds:       '🍼 Bottle',
  breastfeeding:     '🤱 Breast',
  sleepTracking:     '😴 Sleep',
  diapers:           '🧷 Nappies',
  solidFoods:        '🥘 Solids',
  waterIntake:       '💧 Water',
  growthHealth:      '📏 Growth',
  milestones:        '🎯 Milestones',
  doctorVisits:      '🏥 Doctor',
  vaccinations:      '💉 Vaccines',
  allergies:         '⚠️ Allergies',
  foodIntroductions: '🍎 Food intro',
  journalEntries:    '📖 Journal',
};

/**
 * ActivityFeed — a chronological feed of logged entries across all sections.
 *
 * Shows who logged what and when. Supports:
 *  - Collection-type filter (single-select pill tabs)
 *  - Pagination (10 per page, Load more button)
 *
 * Props:
 *   items    — array of activity feed entries from useActivityFeed
 *   loading  — boolean
 */
export default function ActivityFeed({ items, loading }) {
  const { user } = useAuth();
  const [filter, setFilter]   = useState('all');
  const [page, setPage]       = useState(1);

  // Derive the set of collection types present in the feed
  const availableTypes = useMemo(() => {
    const seen = new Set();
    items.forEach((item) => { if (item.collectionName) seen.add(item.collectionName); });
    return [...seen].sort();
  }, [items]);

  // Apply the active filter
  const filtered = useMemo(() => {
    if (filter === 'all') return items;
    return items.filter((item) => item.collectionName === filter);
  }, [items, filter]);

  // Reset to page 1 when filter changes
  const handleFilter = (type) => {
    setFilter(type);
    setPage(1);
  };

  const visible   = filtered.slice(0, page * PAGE_SIZE);
  const hasMore   = visible.length < filtered.length;

  if (loading) {
    return (
      <div className="activity-feed-empty">
        <div className="spinner-border spinner-border-sm text-secondary" role="status">
          <span className="visually-hidden">Loading…</span>
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="activity-feed-empty">
        <div style={{ fontSize: '1.75rem', marginBottom: '0.5rem' }}>📋</div>
        <p>No activity yet. Start logging to see the feed here.</p>
        <p style={{ fontSize: '0.8rem', color: 'var(--text-light)' }}>
          Entries logged by all caregivers will appear here in real time.
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* Filter pills */}
      {availableTypes.length > 1 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginBottom: '0.75rem' }}>
          <button
            onClick={() => handleFilter('all')}
            style={{
              padding: '0.2rem 0.65rem',
              borderRadius: '50px',
              border: '1px solid var(--border-light)',
              fontSize: '0.78rem',
              cursor: 'pointer',
              fontWeight: filter === 'all' ? 700 : 400,
              background: filter === 'all' ? 'var(--primary-dark)' : 'var(--bg-light)',
              color: filter === 'all' ? '#fff' : 'var(--text-dark)',
            }}
          >
            All
          </button>
          {availableTypes.map((type) => (
            <button
              key={type}
              onClick={() => handleFilter(type)}
              style={{
                padding: '0.2rem 0.65rem',
                borderRadius: '50px',
                border: '1px solid var(--border-light)',
                fontSize: '0.78rem',
                cursor: 'pointer',
                fontWeight: filter === type ? 700 : 400,
                background: filter === type ? 'var(--primary-dark)' : 'var(--bg-light)',
                color: filter === type ? '#fff' : 'var(--text-dark)',
              }}
            >
              {FILTER_LABELS[type] ?? type}
            </button>
          ))}
        </div>
      )}

      {/* Empty filtered state */}
      {filtered.length === 0 && (
        <div className="activity-feed-empty">
          <p style={{ fontSize: '0.85rem', color: 'var(--text-light)' }}>
            No entries for this category yet.
          </p>
        </div>
      )}

      {/* Feed items */}
      <div className="activity-feed">
        {visible.map((item) => {
          const isMe = item.loggedBy?.uid === user?.uid;
          const initials = (item.loggedBy?.name || '?')
            .split(' ')
            .map((w) => w[0])
            .join('')
            .slice(0, 2)
            .toUpperCase();

          return (
            <div key={item.id} className="activity-item">
              {/* Left: caregiver avatar */}
              <div
                className={`activity-avatar${isMe ? ' activity-avatar--me' : ''}`}
                title={item.loggedBy?.name || 'Caregiver'}
                aria-label={`Logged by ${item.loggedBy?.name || 'Caregiver'}`}
              >
                {initials}
              </div>

              {/* Middle: entry details */}
              <div className="activity-body">
                <div className="activity-header">
                  <span className="activity-icon" aria-hidden="true">{item.icon}</span>
                  <span className="activity-label">{item.label}</span>
                  <span className="activity-who">
                    {isMe ? 'You' : (item.loggedBy?.name || 'Caregiver')}
                    {item.loggedBy?.role && (
                      <span className="activity-role">
                        {item.loggedBy.role.charAt(0).toUpperCase() + item.loggedBy.role.slice(1)}
                      </span>
                    )}
                  </span>
                </div>
                {item.summary && (
                  <div className="activity-summary">{item.summary}</div>
                )}
              </div>

              {/* Right: time */}
              <div
                className="activity-time"
                title={item.loggedAt ? new Date(item.loggedAt).toLocaleString() : ''}
              >
                {timeAgo(item.loggedAt)}
              </div>
            </div>
          );
        })}
      </div>

      {/* Load more */}
      {hasMore && (
        <div style={{ textAlign: 'center', marginTop: '0.75rem' }}>
          <button
            className="btn btn-sm btn-outline-secondary"
            style={{ borderRadius: '50px', fontSize: '0.82rem' }}
            onClick={() => setPage((p) => p + 1)}
          >
            Load more ({filtered.length - visible.length} remaining)
          </button>
        </div>
      )}

      {/* Total count */}
      <div style={{ textAlign: 'center', marginTop: '0.5rem', fontSize: '0.75rem', color: 'var(--text-light)' }}>
        Showing {Math.min(visible.length, filtered.length)} of {filtered.length} {filter !== 'all' ? 'filtered ' : ''}entries
      </div>
    </div>
  );
}
